import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid, Star, Plus, Trash2, RefreshCw, CheckSquare,
  Square, ChevronDown, ChevronUp, Search, X, BookOpen,
  TrendingUp, Info, List, RotateCcw
} from 'lucide-react';
import Layout from '../components/Layout';
import { userService, recommendationService } from '../services/api';
import toast from 'react-hot-toast';

// ── 섹터 색상 매핑 ─────────────────────────────────────────────────────────────
const SECTOR_COLORS = {
  semiconductor:  { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-700' },
  ai_software:    { bg: 'bg-purple-50',  border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500',  badge: 'bg-purple-100 text-purple-700' },
  ai_infra:       { bg: 'bg-indigo-50',  border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500',  badge: 'bg-indigo-100 text-indigo-700' },
  battery:        { bg: 'bg-green-50',   border: 'border-green-200',  text: 'text-green-700',  dot: 'bg-green-500',   badge: 'bg-green-100 text-green-700' },
  ev_mobility:    { bg: 'bg-teal-50',    border: 'border-teal-200',   text: 'text-teal-700',   dot: 'bg-teal-500',    badge: 'bg-teal-100 text-teal-700' },
  bio_pharma:     { bg: 'bg-pink-50',    border: 'border-pink-200',   text: 'text-pink-700',   dot: 'bg-pink-500',    badge: 'bg-pink-100 text-pink-700' },
  healthcare:     { bg: 'bg-rose-50',    border: 'border-rose-200',   text: 'text-rose-700',   dot: 'bg-rose-500',    badge: 'bg-rose-100 text-rose-700' },
  defense:        { bg: 'bg-gray-50',    border: 'border-gray-300',   text: 'text-gray-700',   dot: 'bg-gray-500',    badge: 'bg-gray-100 text-gray-700' },
  aerospace:      { bg: 'bg-sky-50',     border: 'border-sky-200',    text: 'text-sky-700',    dot: 'bg-sky-500',     badge: 'bg-sky-100 text-sky-700' },
  robot_auto:     { bg: 'bg-cyan-50',    border: 'border-cyan-200',   text: 'text-cyan-700',   dot: 'bg-cyan-500',    badge: 'bg-cyan-100 text-cyan-700' },
  finance:        { bg: 'bg-amber-50',   border: 'border-amber-200',  text: 'text-amber-700',  dot: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-700' },
  fintech:        { bg: 'bg-yellow-50',  border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500',  badge: 'bg-yellow-100 text-yellow-700' },
  energy_chem:    { bg: 'bg-orange-50',  border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500',  badge: 'bg-orange-100 text-orange-700' },
  renewable:      { bg: 'bg-lime-50',    border: 'border-lime-200',   text: 'text-lime-700',   dot: 'bg-lime-500',    badge: 'bg-lime-100 text-lime-700' },
  auto_parts:     { bg: 'bg-stone-50',   border: 'border-stone-200',  text: 'text-stone-700',  dot: 'bg-stone-500',   badge: 'bg-stone-100 text-stone-700' },
  consumer:       { bg: 'bg-fuchsia-50', border: 'border-fuchsia-200',text: 'text-fuchsia-700',dot: 'bg-fuchsia-500', badge: 'bg-fuchsia-100 text-fuchsia-700' },
  media_ent:      { bg: 'bg-violet-50',  border: 'border-violet-200', text: 'text-violet-700', dot: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-700' },
  construction:   { bg: 'bg-red-50',     border: 'border-red-200',    text: 'text-red-700',    dot: 'bg-red-500',     badge: 'bg-red-100 text-red-700' },
  quantum:        { bg: 'bg-emerald-50', border: 'border-emerald-200',text: 'text-emerald-700',dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  etc:            { bg: 'bg-slate-50',   border: 'border-slate-200',  text: 'text-slate-600',  dot: 'bg-slate-400',   badge: 'bg-slate-100 text-slate-600' },
};
const getColor = (key) =>
  SECTOR_COLORS[key] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700' };


// ── 종목 목록 모달 ─────────────────────────────────────────────────────────────
const SectorStocksModal = ({ sector, onClose }) => {
  const [stocks, setStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const c = getColor(sector.sector_key);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        // recommendation-api /api/sectors/stocks?sector_key=xxx
        const res = await fetch(
          `/recommendation-api/api/sectors/stocks?sector_key=${sector.sector_key}&min_confidence=0.5&limit=500`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        const data = await res.json();
        setStocks(data.stocks || []);
      } catch {
        setStocks([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [sector.sector_key]);

  const filtered = stocks.filter(
    (s) =>
      s.stock_name?.includes(searchQuery) ||
      s.stock_code?.includes(searchQuery)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className={`flex items-center gap-3 p-4 rounded-t-2xl ${c.bg} border-b ${c.border}`}>
          <div className={`w-3 h-3 rounded-full ${c.dot}`} />
          <div className="flex-1">
            <h3 className={`font-bold text-base ${c.text}`}>{sector.sector_name}</h3>
            <p className="text-xs text-gray-500">{sector.description}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${c.badge}`}>
            {sector.stock_count}개 종목
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1">
            <X size={18} />
          </button>
        </div>

        {/* 검색 */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-300"
              placeholder="종목명 또는 코드 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* 종목 목록 */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={20} className="animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-400">불러오는 중...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {searchQuery ? '검색 결과가 없습니다' : '종목이 없습니다'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">종목명</th>
                  <th className="text-center px-2 py-2.5 text-xs font-semibold text-gray-500">코드</th>
                  <th className="text-center px-2 py-2.5 text-xs font-semibold text-gray-500">연관도</th>
                  <th className="text-right px-2 py-2.5 text-xs font-semibold text-gray-500">현재가</th>
                  <th className="text-right px-2 py-2.5 text-xs font-semibold text-gray-500">PER</th>
                  <th className="text-right px-2 py-2.5 text-xs font-semibold text-gray-500">PBR</th>
                  <th className="text-right px-2 py-2.5 text-xs font-semibold text-gray-500">시가액</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((s) => (
                  <tr key={s.stock_code} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-gray-800 max-w-[120px] truncate">{s.stock_name}</td>
                    <td className="px-2 py-2.5 text-center">
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{s.stock_code}</span>
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <div className="w-10 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${c.dot}`}
                            style={{ width: `${(s.confidence || 0) * 100}%` }} />
                        </div>
                        <span className="text-xs text-gray-400">{Math.round((s.confidence || 0) * 100)}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-right text-xs text-gray-700">
                      {s.current_price ? s.current_price.toLocaleString() : '-'}
                    </td>
                    <td className="px-2 py-2.5 text-right text-xs">
                      {s.per != null ? (
                        <span className={`${
                          s.per > 0 && s.per < 15 ? 'text-green-600 font-medium' :
                          s.per >= 15 && s.per < 25 ? 'text-gray-600' : 'text-red-400'
                        }`}>{s.per.toFixed(1)}</span>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-2 py-2.5 text-right text-xs">
                      {s.pbr != null ? (
                        <span className={`${
                          s.pbr > 0 && s.pbr < 1 ? 'text-green-600 font-medium' :
                          s.pbr >= 1 && s.pbr < 2 ? 'text-gray-600' : 'text-red-400'
                        }`}>{s.pbr.toFixed(2)}</span>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-2 py-2.5 text-right text-xs text-gray-500">
                      {s.market_cap ? `${(s.market_cap / 1e12).toFixed(1)}조` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 푸터 */}
        <div className="p-3 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
          <span className="text-xs text-gray-400">
            {filtered.length}개 표시 중
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};


// ── 섹터 카드 ──────────────────────────────────────────────────────────────────
// 체크박스 영역 클릭 → 선택/해제
// 카드 나머지 영역 클릭 → 종목 모달
const SectorCard = ({ sector, onToggle, onShowStocks }) => {
  const c = getColor(sector.sector_key);

  return (
    <div
      className={`
        relative rounded-xl border-2 transition-all duration-200
        ${sector.is_selected
          ? `${c.bg} ${c.border} shadow-sm`
          : 'bg-white border-gray-200 opacity-60'
        }
      `}
    >
      <div className="p-3 flex items-center gap-3">
        {/* ── 체크박스 영역: 클릭 = 선택/해제 ── */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(sector.id, !sector.is_selected); }}
          className={`flex-shrink-0 transition-colors ${sector.is_selected ? c.text : 'text-gray-300'} hover:scale-110`}
        >
          {sector.is_selected ? <CheckSquare size={20} /> : <Square size={20} />}
        </button>

        {/* 색상 점 */}
        <div className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${c.dot}`} />

        {/* ── 나머지 영역: 클릭 = 종목 모달 ── */}
        <button
          type="button"
          onClick={() => onShowStocks(sector)}
          className="flex-1 min-w-0 text-left group"
        >
          <div className={`font-semibold text-sm transition-colors
            ${sector.is_selected ? c.text : 'text-gray-400'}
            group-hover:underline`}
          >
            {sector.sector_name}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-xs text-gray-400">{sector.stock_count}개 종목</span>
            <List size={10} className="text-gray-300" />
          </div>
        </button>
      </div>
    </div>
  );
};


// ── 관심 종목 행 ───────────────────────────────────────────────────────────────
const WatchlistRow = ({ item, onRemove, onMemoSave }) => {
  const [editing, setEditing] = useState(false);
  const [memo, setMemo] = useState(item.memo || '');

  const handleSave = async () => {
    await onMemoSave(item.stock_code, memo);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-800">{item.stock_name}</span>
          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
            {item.stock_code}
          </span>
          {item.is_in_pool && (
            <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
              매매 pool
            </span>
          )}
        </div>
        {editing ? (
          <div className="flex items-center gap-1 mt-1">
            <input
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-blue-300"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="메모 입력..."
              autoFocus
            />
            <button onClick={handleSave} className="text-xs text-blue-600 hover:text-blue-800 px-1">저장</button>
            <button onClick={() => setEditing(false)} className="text-xs text-gray-400 px-1">취소</button>
          </div>
        ) : (
          <div
            className="text-xs text-gray-400 mt-0.5 cursor-pointer hover:text-gray-600"
            onClick={() => setEditing(true)}
          >
            {memo || '메모 추가...'}
          </div>
        )}
      </div>
      <button
        onClick={() => onRemove(item.stock_code)}
        className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
};


// ── 종목 검색 추가 모달 ────────────────────────────────────────────────────────
const AddStockModal = ({ onAdd, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [memo, setMemo] = useState('');

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/trading-api/trade/search/${encodeURIComponent(query.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      // TradingPage 와 동일한 응답 구조: { success, results: [{stock_code, stock_name, ...}] }
      setResults(data.results || data.stocks || data.data || []);
    } catch {
      toast.error('종목 검색 실패');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-800">관심 종목 추가</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-4">
          <div className="flex gap-2 mb-3">
            <input
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300"
              placeholder="종목명 또는 코드 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch} disabled={isSearching}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
            >
              {isSearching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1 mb-3">
            {results.map((stock) => (
              <button
                key={stock.stock_code || stock.code}
                onClick={() => onAdd({
                  stock_code: stock.stock_code || stock.code,
                  stock_name: stock.stock_name || stock.name,
                  memo,
                })}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <span className="font-semibold text-sm text-gray-800">
                  {stock.stock_name || stock.name}
                </span>
                <span className="text-xs text-gray-400 ml-2">
                  {stock.stock_code || stock.code}
                </span>
              </button>
            ))}
            {results.length === 0 && query && !isSearching && (
              <div className="text-center text-sm text-gray-400 py-4">검색 결과가 없습니다</div>
            )}
          </div>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300"
            placeholder="메모 (선택)"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};


// ── 메인 페이지 ───────────────────────────────────────────────────────────────
const SectorManagementPage = () => {
  const [sectors, setSectors] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sector');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [stocksModal, setStocksModal] = useState(null);
  const [scoringStatus, setScoringStatus] = useState(null);
  const [isReselecting, setIsReselecting] = useState(false); // 종목 모달 대상 섹터

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [sectorRes, watchlistRes, statusRes] = await Promise.all([
        userService.getSectors(),
        userService.getWatchlist(),
        userService.getScoringStatus().catch(() => null),
      ]);
      const sectorList = sectorRes.data?.sectors || [];
      setSectors(sectorList);
      setSelectedCount(sectorList.filter((s) => s.is_selected).length);
      setWatchlist(watchlistRes.data?.watchlist || []);
      if (statusRes?.data) setScoringStatus(statusRes.data);
    } catch {
      toast.error('데이터 로드 실패');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // 포트폴리오 즉시 반영
  const handleReselect = async () => {
    setIsReselecting(true);
    try {
      await userService.refreshPortfolio();
      toast.success('섹터 설정이 포트폴리오에 반영됩니다.', { duration: 4000 });
    } catch {
      toast.error('재생성 실패');
    } finally {
      setIsReselecting(false);
    }
  };

  const handleSectorToggle = async (sectorId, newSelected) => {
    setSectors((prev) => prev.map((s) => s.id === sectorId ? { ...s, is_selected: newSelected } : s));
    setSelectedCount((prev) => newSelected ? prev + 1 : prev - 1);
    try {
      await userService.updateSectors([sectorId], newSelected);
    } catch {
      setSectors((prev) => prev.map((s) => s.id === sectorId ? { ...s, is_selected: !newSelected } : s));
      setSelectedCount((prev) => newSelected ? prev - 1 : prev + 1);
      toast.error('섹터 변경 실패');
    }
  };

  const handleSelectAll = async (select) => {
    setIsSaving(true);
    try {
      if (select) await userService.selectAllSectors();
      else await userService.deselectAllSectors();
      await loadData();
      toast.success(select ? '전체 섹터 선택' : '전체 섹터 해제');
    } catch {
      toast.error('변경 실패');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWatchlist = async (data) => {
    try {
      await userService.addWatchlist(data);
      setShowAddModal(false);
      await loadData();
      toast.success(`${data.stock_name} 관심 종목 추가`);
    } catch {
      toast.error('추가 실패');
    }
  };

  const handleRemoveWatchlist = async (stockCode) => {
    if (!window.confirm('관심 종목에서 삭제하시겠습니까?')) return;
    try {
      await userService.removeWatchlist(stockCode);
      setWatchlist((prev) => prev.filter((w) => w.stock_code !== stockCode));
      toast.success('삭제되었습니다');
    } catch {
      toast.error('삭제 실패');
    }
  };

  const handleMemoSave = async (stockCode, memo) => {
    try {
      await userService.updateWatchlistMemo(stockCode, memo);
      setWatchlist((prev) => prev.map((w) => w.stock_code === stockCode ? { ...w, memo } : w));
      toast.success('메모 저장');
    } catch {
      toast.error('메모 저장 실패');
    }
  };

  const activeWatchlist = watchlist.filter((w) => w.is_active);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* 헤더 */}
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Grid size={20} className="text-blue-500" />
                투자 섹터 설정
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                선택한 섹터의 종목만 매매 후보에 포함됩니다
              </p>
              {scoringStatus?.last_run && (
                <p className="text-xs text-gray-400 mt-1">
                  Top 100 마지막 업데이트: {new Date(scoringStatus.last_run).toLocaleString()} ({scoringStatus.total}개)
                </p>
              )}
            </div>
            <button
              onClick={handleReselect}
              disabled={isReselecting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RotateCcw size={14} className={isReselecting ? 'animate-spin' : ''} />
              {isReselecting ? '적용 중...' : '지금 바로 적용'}
            </button>
          </div>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl">
          {[
            { key: 'sector',    label: '섹터 선택',  icon: <Grid size={14} /> },
            { key: 'watchlist', label: '관심 종목',  icon: <Star size={14} /> },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all
                ${activeTab === tab.key ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* ── 섹터 탭 ──────────────────────────────────────────────────────── */}
        {activeTab === 'sector' && (
          <>
            {/* 요약 바 */}
            <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-blue-500" />
                <span className="text-sm text-blue-700 font-medium">
                  {selectedCount} / {sectors.length}개 섹터 선택됨
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleSelectAll(true)} disabled={isSaving}
                  className="text-xs px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors">
                  전체 선택
                </button>
                <button onClick={() => handleSelectAll(false)} disabled={isSaving}
                  className="text-xs px-3 py-1.5 bg-white text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  전체 해제
                </button>
              </div>
            </div>

            {/* 안내 */}
            <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <Info size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>체크박스</strong>를 클릭하면 섹터를 선택/해제합니다.
                <strong> 종목명</strong>을 클릭하면 해당 섹터의 종목 목록을 볼 수 있습니다.
              </p>
            </div>

            {/* 섹터 그리드 */}
            {isLoading ? (
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {sectors.map((sector) => (
                  <SectorCard
                    key={sector.id}
                    sector={sector}
                    onToggle={handleSectorToggle}
                    onShowStocks={setStocksModal}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── 관심 종목 탭 ─────────────────────────────────────────────────── */}
        {activeTab === 'watchlist' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{activeWatchlist.length}개 종목</span>
                <span className="text-xs text-gray-400">(Box권 조건 충족 시 자동 매매 pool 포함)</span>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
              >
                <Plus size={14} />종목 추가
              </button>
            </div>

            <div className="flex items-start gap-2 mb-4 p-3 bg-green-50 rounded-xl border border-green-100">
              <BookOpen size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700 leading-relaxed">
                관심 종목은 Top 100 스코어링과 무관하게 Box권 조건만 충족하면 매매합니다.
                섹터 설정을 해제해도 관심 종목은 항상 매매 pool에 포함됩니다.
              </p>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            ) : activeWatchlist.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Star size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">관심 종목이 없습니다</p>
                <p className="text-xs mt-1">+ 종목 추가 버튼으로 등록하세요</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 px-4">
                {activeWatchlist.map((item) => (
                  <WatchlistRow
                    key={item.stock_code}
                    item={item}
                    onRemove={handleRemoveWatchlist}
                    onMemoSave={handleMemoSave}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 관심 종목 추가 모달 */}
      {showAddModal && (
        <AddStockModal onAdd={handleAddWatchlist} onClose={() => setShowAddModal(false)} />
      )}

      {/* 섹터 종목 모달 */}
      {stocksModal && (
        <SectorStocksModal sector={stocksModal} onClose={() => setStocksModal(null)} />
      )}
    </Layout>
  );
};

export default SectorManagementPage;
